/**
 * Kleiner ⌄-Button "Zum Kalender" mit Dropdown:
 *   - .ics-Datei herunterladen (universal, iOS / Android / Mac / Outlook)
 *   - Google Calendar (Browser-Link)
 *   - Outlook Calendar (Browser-Link)
 *
 * Description enthaelt den Teilnehmer-Link (auch im Admin-View).
 */

import { useEffect, useRef, useState } from "react";
import {
  CalendarPlus,
  CaretDown,
  Download,
} from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";
import {
  buildEventCalendar,
  downloadIcs,
  googleCalendarUrl,
  outlookCalendarUrl,
} from "@/lib/calendar";
import styles from "./AddToCalendarMenu.module.css";

type Props = {
  shortcut: string;
  title: string;
  startIso: string;
  location: string | null;
  description: string | null;
  /** Der oeffentliche Gast-Link. Auch fuer Admins — Admin-Link landet NIE im Kalender. */
  guestUrl: string;
};

export function AddToCalendarMenu({
  shortcut,
  title,
  startIso,
  location,
  description,
  guestUrl,
}: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const calEvent = buildEventCalendar({
    shortcut,
    title,
    startIso,
    location,
    description,
    guestUrl,
    listLabel: t("event.calendarListLabel"),
  });

  const sanitizedFilename =
    title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "")
      .slice(0, 40) || "event";

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <CalendarPlus size={16} weight="regular" />
        <span>{t("event.addToCalendar")}</span>
        <CaretDown size={12} weight="bold" />
      </button>

      {open && (
        <div className={styles.menu} role="menu">
          <button
            type="button"
            role="menuitem"
            className={styles.item}
            onClick={() => {
              downloadIcs(calEvent, `${sanitizedFilename}.ics`);
              setOpen(false);
            }}
          >
            <Download size={16} weight="regular" />
            <span>{t("event.calendarIcs")}</span>
            <span className={styles.itemHint}>{t("event.calendarIcsHint")}</span>
          </button>
          <a
            href={googleCalendarUrl(calEvent)}
            target="_blank"
            rel="noopener noreferrer"
            role="menuitem"
            className={styles.item}
            onClick={() => setOpen(false)}
          >
            <CalendarPlus size={16} weight="regular" />
            <span>Google Calendar</span>
          </a>
          <a
            href={outlookCalendarUrl(calEvent)}
            target="_blank"
            rel="noopener noreferrer"
            role="menuitem"
            className={styles.item}
            onClick={() => setOpen(false)}
          >
            <CalendarPlus size={16} weight="regular" />
            <span>Outlook</span>
          </a>
        </div>
      )}
    </div>
  );
}
